import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Dropzone from 'react-dropzone';
import {
  Plus, Send, Trash2, Receipt, DollarSign, Search, RefreshCw,
  Calendar, Check, Clock, AlertTriangle, Upload, X, Camera,
  GripVertical, CreditCard, LayoutGrid, List as ListIcon,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ReactDOM from 'react-dom';
import PageTitle from '../../../layouts/PageTitle';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as api from '../../../../services/api/expensesApi';
import ExpenseDetailModal from './ExpenseDetailModal';
import * as projectsApi from '../../../../services/api/projectsApi';
import * as S from '../../Common/internalToolsStyles';
import { format } from 'date-fns';

// ─── SWEDISH EXPENSE CATEGORIES ───

// Skatteverket schablonbelopp 2026 — kept in the UI for *display only* so the
// preview amounts match what the backend computes. Authoritative source is
// backend utils/swedishTaxRates.js — don't let these two drift.
const TAX_RATES_2026 = {
  traktamente: { fullDay: 290, halfDay: 145, night: 145, breakfast: 70, lunch: 116, dinner: 116 },
  mileage:     { own: 25, companyGas: 12, companyEV: 9.50 },
  representation: { simpleLimit: 60, mealVatLimit: 300 },
};

const CATEGORIES = [
  { value: 'travel', label: 'Resa', icon: '✈️' },
  { value: 'taxi', label: 'Taxi', icon: '🚕' },
  { value: 'accommodation', label: 'Boende', icon: '🏨' },
  { value: 'meals', label: 'Mat & dryck', icon: '🍽️' },
  { value: 'fika', label: 'Fika / enklare förtäring', icon: '☕' },
  { value: 'representation', label: 'Representation', icon: '🤝' },
  { value: 'mileage', label: 'Milersättning', icon: '🚗' },
  { value: 'parking', label: 'Parkering', icon: '🅿️' },
  { value: 'fuel', label: 'Drivmedel', icon: '⛽' },
  { value: 'software', label: 'Programvara', icon: '💻' },
  { value: 'equipment', label: 'Utrustning', icon: '🔧' },
  { value: 'materials', label: 'Material', icon: '📦' },
  { value: 'phone_internet', label: 'Telefon & internet', icon: '📱' },
  { value: 'training', label: 'Utbildning', icon: '📚' },
  { value: 'conference', label: 'Konferens', icon: '🎤' },
  { value: 'wellness', label: 'Friskvård', icon: '🏃' },
  { value: 'other', label: 'Övrigt', icon: '📋' },
];

const catMap = {};
CATEGORIES.forEach(c => { catMap[c.value] = c; });

// Swedish statutory VAT rates. 0% means exempt (rare — banking, utländskt,
// healthcare); most receipts are 25%. Snapping OCR-derived rates to the
// closest statutory band avoids odd values like 24.9 from rounding errors.
const VAT_RATES = [25, 12, 6, 0];
const nearestVatRate = (r) => {
  if (r == null || Number.isNaN(Number(r))) return 25;
  return VAT_RATES.reduce((best, v) => Math.abs(v - r) < Math.abs(best - r) ? v : best, 25);
};

// ─── RECEIPT THUMBNAIL ───
// Creates a blob: URL from the picked File so the user can eyeball what got
// uploaded. Images render inline, PDFs show an icon + filename + size (we
// don't bundle a PDF renderer on this page — that lives in Documents only).
const ReceiptThumbnail = ({ file }) => {
  const [objectUrl, setObjectUrl] = React.useState(null);
  React.useEffect(() => {
    if (!file || !file.type?.startsWith('image/')) return undefined;
    const u = URL.createObjectURL(file);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const isImage = file?.type?.startsWith('image/');
  const isPdf = file?.type === 'application/pdf';
  const sizeText = file?.size
    ? file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} kB` : `${(file.size / (1024 * 1024)).toFixed(2)} MB`
    : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', width: '100%' }}>
      <div style={{
        width: 64, height: 80, borderRadius: 6, overflow: 'hidden',
        background: '#f3f4f6', border: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#9ca3af', flexShrink: 0,
      }}>
        {isImage && objectUrl ? (
          <img src={objectUrl} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : isPdf ? (
          <Receipt size={28} />
        ) : (
          <Receipt size={28} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={14} style={{ color: 'var(--primary)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>Kvitto uppladdat</span>
        </div>
        <div style={{ fontSize: 12, color: '#111827', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {file?.name}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
          {sizeText}{sizeText && ' · '}Klicka för att byta
        </div>
      </div>
    </div>
  );
};

// ─── CATEGORY-SPECIFIC SUB-FORM ───
// Inlined rather than a separate file — small, read-only, and the form state
// lives on the parent. Visible only when category triggers sub-rules.

function ExpenseSubForm({ form, setForm }) {
  const { category, metadata, amount } = form;
  const md = metadata || {};

  const setMd = (patch) => setForm({ ...form, metadata: { ...md, ...patch } });

  // ─── MILEAGE ───
  if (category === 'mileage') {
    const distance = parseFloat(md.distance_km) || 0;
    const vehicle = md.vehicle_type || 'own';
    const miles = distance / 10;
    const rate = vehicle === 'company_ev' ? TAX_RATES_2026.mileage.companyEV
      : vehicle === 'company_gas' || vehicle === 'company_diesel' ? TAX_RATES_2026.mileage.companyGas
        : TAX_RATES_2026.mileage.own;
    const schablon = miles * rate;
    const paid = parseFloat(amount) || 0;
    const taxable = Math.max(0, paid - schablon);

    return (
      <div style={{ padding: 12, background: '#f5f9ff', borderRadius: 8, marginBottom: 16, border: '1px solid #d3e2f7' }}>
        <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 8 }}>🚗 Bilersättning — schablon enligt Skatteverket 2026</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={S.modalLabel}>Sträcka (km)</label>
            <Form.Control type="number" step="1" min="0" value={md.distance_km || ''}
              onChange={(e) => setMd({ distance_km: e.target.value })}
              style={{ borderRadius: 8, fontSize: 14, height: 38 }}
            />
          </div>
          <div>
            <label style={S.modalLabel}>Fordonstyp</label>
            <Form.Select value={vehicle} onChange={(e) => setMd({ vehicle_type: e.target.value })}
              style={{ borderRadius: 8, fontSize: 14, height: 38 }}>
              <option value="own">Egen bil (25 kr/mil)</option>
              <option value="company_gas">Tjänstebil bensin/diesel (12 kr/mil)</option>
              <option value="company_ev">Tjänstebil el (9,50 kr/mil)</option>
            </Form.Select>
          </div>
        </div>
        {distance > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#075985' }}>
            Skattefri schablon: <strong>{schablon.toFixed(2)} kr</strong>
            {taxable > 0 && <> — överskott <strong>{taxable.toFixed(2)} kr</strong> blir skattepliktig</>}
          </div>
        )}
      </div>
    );
  }

  // ─── TRAKTAMENTE (opt-in via travel/meals category) ───
  if (category === 'travel' || category === 'meals' || category === 'accommodation') {
    return (
      <div style={{ padding: 12, background: '#fffaf0', borderRadius: 8, marginBottom: 16, border: '1px solid #fde68a' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#92400e' }}>
          <input type="checkbox" checked={!!md.is_traktamente}
            onChange={(e) => setMd({ is_traktamente: e.target.checked })}
          />
          🧾 Detta är ett traktamente (dagtraktamente enligt Skatteverket)
        </label>
        {md.is_traktamente && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={S.modalLabel}>Hela dagar (290 kr)</label>
                <Form.Control type="number" min="0" value={md.day_count_full || ''}
                  onChange={(e) => setMd({ day_count_full: parseInt(e.target.value, 10) || 0 })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
              <div>
                <label style={S.modalLabel}>Halva dagar (145 kr)</label>
                <Form.Control type="number" min="0" value={md.day_count_half || ''}
                  onChange={(e) => setMd({ day_count_half: parseInt(e.target.value, 10) || 0 })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
              <div>
                <label style={S.modalLabel}>Nätter (145 kr)</label>
                <Form.Control type="number" min="0" value={md.night_count || ''}
                  onChange={(e) => setMd({ night_count: parseInt(e.target.value, 10) || 0 })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#78350f', fontWeight: 600 }}>
              Kostavdrag (fria måltider under resan)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 4 }}>
              <div>
                <label style={S.modalLabel}>Frukost (-70 kr/st)</label>
                <Form.Control type="number" min="0" value={md.meal_deductions?.breakfast || ''}
                  onChange={(e) => setMd({ meal_deductions: { ...(md.meal_deductions || {}), breakfast: parseInt(e.target.value, 10) || 0 } })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
              <div>
                <label style={S.modalLabel}>Lunch (-116 kr/st)</label>
                <Form.Control type="number" min="0" value={md.meal_deductions?.lunch || ''}
                  onChange={(e) => setMd({ meal_deductions: { ...(md.meal_deductions || {}), lunch: parseInt(e.target.value, 10) || 0 } })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
              <div>
                <label style={S.modalLabel}>Middag (-116 kr/st)</label>
                <Form.Control type="number" min="0" value={md.meal_deductions?.dinner || ''}
                  onChange={(e) => setMd({ meal_deductions: { ...(md.meal_deductions || {}), dinner: parseInt(e.target.value, 10) || 0 } })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#92400e', marginTop: 12 }}>
              <input type="checkbox" checked={!!md.is_foreign}
                onChange={(e) => setMd({ is_foreign: e.target.checked })}
              />
              Utlandstraktamente (egen schablon per land — hanteras manuellt)
            </label>
            {md.is_foreign && (
              <div style={{ marginTop: 8 }}>
                <label style={S.modalLabel}>Destinationsland</label>
                <Form.Control type="text" value={md.destination_country || ''}
                  onChange={(e) => setMd({ destination_country: e.target.value })}
                  style={{ borderRadius: 8, fontSize: 14, height: 38 }} placeholder="T.ex. Tyskland"
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ─── REPRESENTATION / FIKA ───
  if (category === 'representation' || category === 'fika') {
    const participants = Math.max(1, parseInt(md.participants, 10) || 1);
    const perPerson = (parseFloat(amount) || 0) / participants;
    const isFika = category === 'fika';
    const mealType = md.meal_type || (isFika ? 'simple_refreshment' : 'meal');
    const isExternal = md.is_external !== false;

    const simpleLimit = TAX_RATES_2026.representation.simpleLimit;
    const mealLimit = TAX_RATES_2026.representation.mealVatLimit;

    let hint = null;
    if (mealType === 'simple_refreshment') {
      hint = perPerson <= simpleLimit
        ? `✓ Under ${simpleLimit} kr/person — avdragsgill som enklare förtäring (1220).`
        : `⚠ Över ${simpleLimit} kr/person — hela beloppet blir ej avdragsgill (1221).`;
    } else {
      hint = perPerson <= mealLimit
        ? `✓ Under ${mealLimit} kr/person — moms avdragsgill (1220).`
        : `⚠ ${mealLimit} kr/person avdragsgill (moms), överskott ej avdragsgill (1221).`;
    }

    return (
      <div style={{ padding: 12, background: '#fdf4ff', borderRadius: 8, marginBottom: 16, border: '1px solid #e9d5ff' }}>
        <div style={{ fontSize: 12, color: '#6b21a8', fontWeight: 600, marginBottom: 8 }}>
          🤝 Representation — 2026 gränser (Skatteverket)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={S.modalLabel}>Antal personer</label>
            <Form.Control type="number" min="1" value={md.participants || ''}
              onChange={(e) => setMd({ participants: parseInt(e.target.value, 10) || 1 })}
              style={{ borderRadius: 8, fontSize: 14, height: 38 }}
            />
          </div>
          <div>
            <label style={S.modalLabel}>Typ</label>
            <Form.Select value={mealType} onChange={(e) => setMd({ meal_type: e.target.value })}
              style={{ borderRadius: 8, fontSize: 14, height: 38 }}>
              <option value="simple_refreshment">Enklare förtäring (fika)</option>
              <option value="meal">Måltid (lunch/middag)</option>
              <option value="gift">Representationsgåva</option>
            </Form.Select>
          </div>
          <div>
            <label style={S.modalLabel}>Extern/intern</label>
            <Form.Select value={isExternal ? 'external' : 'internal'}
              onChange={(e) => setMd({ is_external: e.target.value === 'external' })}
              style={{ borderRadius: 8, fontSize: 14, height: 38 }}>
              <option value="external">Extern (kund/leverantör)</option>
              <option value="internal">Intern (personal)</option>
            </Form.Select>
          </div>
        </div>
        {participants > 0 && amount > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b21a8' }}>
            {perPerson.toFixed(2)} kr/person — {hint}
          </div>
        )}
      </div>
    );
  }

  return null;
}


const fmtAmount = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

// ─── LANE CONFIG ───

// Lanes — the employee's own payment trail. `paid` combines the two
// post-approval states (booked + settled) so the lane stays readable; the
// card itself shows a subtle badge to differentiate "bokförd" vs "betald".
const LANES = [
  { id: 'draft',     label: 'Utkast',     color: '#6b7280', bg: '#f3f4f6',               icon: Clock,        match: (s) => s === 'draft' },
  { id: 'submitted', label: 'Inskickade', color: '#d97706', bg: '#fffbeb',               icon: Send,         match: (s) => s === 'submitted' },
  { id: 'approved',  label: 'Godkända',   color: '#059669', bg: 'rgba(5,150,105,0.06)',  icon: Check,        match: (s) => s === 'approved' },
  { id: 'paid',      label: 'Betalda',    color: '#1e40af', bg: 'rgba(37,99,235,0.06)',  icon: CreditCard,   match: (s) => s === 'booked' || s === 'settled' },
  { id: 'rejected',  label: 'Avvisade',   color: '#dc2626', bg: 'rgba(239,68,68,0.06)',  icon: AlertTriangle, match: (s) => s === 'rejected' },
];

const DRAG_TRANSITIONS = {
  draft: ['submitted'],
  submitted: ['approved', 'rejected'],
  approved: [],
  rejected: [],
};

// ─── EXPENSE CARD ───

const ExpenseCard = ({ expense, isDragging, onDelete, onOpen }) => {
  const cat = catMap[expense.category] || { label: expense.category, icon: '📋' };
  const canDelete = expense.status === 'draft' && !expense.payroll_id && !expense.invoice_item_id;
  const isDragDisabled = DRAG_TRANSITIONS[expense.status]?.length === 0;

  return (
    <div
      className={`expense-card${isDragging ? ' is-dragging' : ''}${isDragDisabled ? ' drag-disabled' : ''}`}
      onClick={() => onOpen?.(expense)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(expense); } }}
    >
      <div className="d-flex align-items-start gap-2 mb-2">
        <span style={{ fontSize: '1rem', lineHeight: 1 }}>{cat.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--title)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {expense.description}
          </div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>{cat.label}</div>
        </div>
        {!isDragDisabled && <GripVertical size={14} className="drag-handle" />}
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>
          <Calendar size={10} style={{ opacity: 0.5 }} />
          {format(new Date(expense.expense_date), 'd MMM yyyy')}
        </div>
        <span style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--title)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtAmount(expense.amount)} <span style={{ fontSize: '0.625rem', fontWeight: 400, color: 'var(--secondary)' }}>{expense.currency || 'SEK'}</span>
        </span>
      </div>

      {/* Tags */}
      <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
        {expense.project_name && (
          <span style={{ fontSize: '0.5625rem', padding: '1px 5px', borderRadius: 3, background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
            {expense.project_name}
          </span>
        )}
        {expense.is_billable && (
          <span style={{ fontSize: '0.5625rem', padding: '1px 5px', borderRadius: 3, background: 'rgba(5,150,105,0.08)', color: '#059669' }}>
            Debiterbar
          </span>
        )}
        {expense.receipt_url && (
          <span style={{ fontSize: '0.5625rem', padding: '1px 5px', borderRadius: 3, background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }}>
            Kvitto
          </span>
        )}
        {/* Distinguish booked (on the books, awaiting payout) vs settled
            (money's out) inside the shared "Betalda" lane. */}
        {expense.status === 'booked' && (
          <span style={{ fontSize: '0.5625rem', padding: '1px 5px', borderRadius: 3, background: 'rgba(37,99,235,0.08)', color: '#1e40af' }}>
            Bokförd
          </span>
        )}
        {expense.status === 'settled' && (
          <span style={{ fontSize: '0.5625rem', padding: '1px 5px', borderRadius: 3, background: 'rgba(5,150,105,0.10)', color: '#065f46' }}>
            Betald
          </span>
        )}
      </div>

      {expense.rejection_reason && (
        <div style={{ marginTop: 6, padding: '4px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.06)', fontSize: '0.625rem', color: '#dc2626', lineHeight: 1.3 }}>
          <AlertTriangle size={9} style={{ marginRight: 3, verticalAlign: -1 }} />
          {expense.rejection_reason}
        </div>
      )}

      {canDelete && (
        <div style={{ marginTop: 6, textAlign: 'right' }}>
          <button
            onClick={e => { e.stopPropagation(); onDelete(expense.expense_id); }}
            style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: 2, fontSize: '0.625rem' }}
            onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
            onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ───

const ExpensesPage = () => {
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detailExpense, setDetailExpense] = useState(null);

  // Default form values in one place so we can reset cleanly on
  // close/cancel — closing the modal should not leak previous input into
  // the next "Nytt utlägg" opening.
  const emptyForm = () => ({
    expenseDate: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    // VAT first-class — required on submit. Prefilled from OCR when DI
    // returns it; otherwise the user has to enter them manually. Store as
    // strings for input binding; coerced to numbers on submit.
    vatAmount: '',
    vatRate: '25',
    category: 'other',
    description: '',
    projectId: '',
    isBillable: false,
    isReimbursable: true,
    receiptFile: null,
    receiptUrl: null,
    receiptAttachmentId: null,
    metadata: null,
  });
  const closeForm = () => {
    setForm(emptyForm());
    setShowForm(false);
  };
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dragSourceStatus, setDragSourceStatus] = useState(null);
  // View toggle — persists per browser, mirrors the pattern used on
  // ApprovalPage / MyPeriodsBoard / ExpenseApprovalQueue so the surfaces
  // feel like the same product.
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('expensesViewMode') || 'kanban');
  const changeView = (m) => { setViewMode(m); try { localStorage.setItem('expensesViewMode', m); } catch {} };
  // List view's own status pill filter — kanban already shows every lane.
  // Defaults to 'all' so the list isn't empty when the user has nothing in
  // a specific status (matches the time-reporting fix).
  const [listStatus, setListStatus] = useState('all');
  // Per-lane recency filter for terminal lanes (approved / paid /
  // rejected). 30 days keeps the lane focused on the recent month
  // without losing access to older items via the dropdown. Same pattern
  // and defaults as the time-reporting kanbans.
  const [approvedDays, setApprovedDays] = useState(30);
  const [paidDays, setPaidDays] = useState(30);
  const [rejectedDays, setRejectedDays] = useState(30);
  const [scanning, setScanning] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const [form, setForm] = useState({
    expenseDate: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    category: 'other',
    description: '',
    projectId: '',
    isBillable: false,
    isReimbursable: true,
    receiptFile: null,
    receiptUrl: null,
    receiptAttachmentId: null,
    // Sub-type metadata — only populated when category triggers a sub-form
    metadata: null,
  });

  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const [expRes, projRes] = await Promise.all([
        api.getExpenses(companyId, {}),
        projectsApi.getMyProjects(companyId),
      ]);
      setExpenses(expRes.data.expenses || []);
      setProjects(projRes.data.projects || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda data');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleScanReceipt = async (file) => {
    try {
      setScanning(true);
      const res = await api.analyzeReceipt(companyId, file);
      const d = res.data.extracted;
      setForm(prev => ({
        ...prev,
        amount: d.amount != null ? String(d.amount) : prev.amount,
        // OCR prefill for VAT. Rate is snapped to the nearest Swedish
        // statutory rate so the dropdown binds cleanly.
        vatAmount: d.vatAmount != null ? String(d.vatAmount) : prev.vatAmount,
        vatRate: d.vatRate != null ? String(nearestVatRate(d.vatRate)) : prev.vatRate,
        expenseDate: d.date || prev.expenseDate,
        description: d.description || d.vendor || prev.description,
        receiptFile: file,
        receiptUrl: res.data.receiptUrl || null,
        // attachmentId: the temp attachments row backing this blob. Sent on
        // createExpense so the backend can UPDATE it to status='linked'.
        receiptAttachmentId: res.data.attachmentId || null,
        // Stash the rich OCR payload under metadata.ocr so supplier
        // auto-match / VAT reporting / BAS-account hinting downstream can
        // use it. The visible form only reads a couple of these fields; the
        // rest lives quietly in expense.metadata.
        metadata: {
          ...(prev.metadata || {}),
          ocr: {
            subtotal: d.subtotal,
            vatAmount: d.vatAmount,
            vatRate: d.vatRate,
            taxDetails: d.taxDetails,
            vendor: d.vendor,
            vendorAddress: d.vendorAddress,
            vendorTaxId: d.vendorTaxId,
            invoiceNumber: d.invoiceNumber,
            dueDate: d.dueDate,
            items: d.items,
            confidence: d.confidence,
          },
        },
      }));
    } catch (err) {
      // OCR failed — still keep the file, user can fill manually (no blob URL)
      setForm(prev => ({ ...prev, receiptFile: file, receiptUrl: null, receiptAttachmentId: null }));
    } finally {
      setScanning(false);
    }
  };

  const handleUploadReceiptOnly = async (file) => {
    // User drops a file without wanting OCR — still persist to blob so receipt_url
    // is saved with the expense.
    try {
      setScanning(true);
      const res = await api.analyzeReceipt(companyId, file);
      setForm(prev => ({
        ...prev,
        receiptFile: file,
        receiptUrl: res.data.receiptUrl || null,
        receiptAttachmentId: res.data.attachmentId || null,
      }));
    } catch (err) {
      setForm(prev => ({ ...prev, receiptFile: file, receiptUrl: null, receiptAttachmentId: null }));
    } finally {
      setScanning(false);
    }
  };

  const handleCreate = async () => {
    // Validation: amount + description always required; VAT must be
    // present (can be 0 kr with rate=0% for exempt cases).
    if (!form.description || !form.amount) return;
    if (form.vatAmount === '' || form.vatRate === '') {
      setError('Moms och momssats måste anges.');
      return;
    }
    try {
      setSaving(true);
      // Attach confirmed VAT under metadata.vat (separate from the raw OCR
      // payload under metadata.ocr — the user-confirmed values are the
      // source of truth for booking).
      const mergedMetadata = {
        ...(form.metadata || {}),
        vat: {
          ...(form.metadata?.vat || {}), // preserve scheme (domestic|eu_reverse|import)
          amount: parseFloat(form.vatAmount) || 0,
          rate: parseInt(form.vatRate, 10) || 0,
        },
      };
      await api.createExpense(companyId, {
        expenseDate: form.expenseDate,
        amount: parseFloat(form.amount),
        vatAmount: parseFloat(form.vatAmount) || 0,
        vatRate: parseInt(form.vatRate, 10) || 0,
        category: form.category,
        description: form.description,
        projectId: form.projectId || null,
        isBillable: form.isBillable,
        isReimbursable: form.isReimbursable,
        receiptUrl: form.receiptUrl || null,
        attachmentId: form.receiptAttachmentId || null,
        metadata: mergedMetadata,
      });
      closeForm();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await api.deleteExpense(companyId, expenseId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ta bort');
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      setSaving(true);
      await api.approveExpense(companyId, expenseId);
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte godkänna'); }
    finally { setSaving(false); }
  };

  const handleReject = async (expenseId) => {
    if (!rejectReason.trim()) return;
    try {
      setSaving(true);
      await api.rejectExpense(companyId, expenseId, rejectReason);
      setRejectingId(null); setRejectReason('');
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte avvisa'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async (expenseId) => {
    try { setSaving(true); await api.submitExpense(companyId, expenseId); await loadData(); }
    catch (err) { setError(err.response?.data?.message || 'Kunde inte skicka in'); }
    finally { setSaving(false); }
  };

  // Filter + group
  const filtered = useMemo(() => {
    let items = expenses;
    if (categoryFilter !== 'all') items = items.filter(e => e.category === categoryFilter);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(e =>
        (e.description && e.description.toLowerCase().includes(q)) ||
        (e.project_name && e.project_name.toLowerCase().includes(q)) ||
        (e.expense_date && e.expense_date.includes(q))
      );
    }
    return items;
  }, [expenses, categoryFilter, searchTerm]);

  const { grouped, laneTotals } = useMemo(() => {
    const g = {};
    for (const lane of LANES) g[lane.id] = [];
    for (const e of filtered) {
      const lane = LANES.find((l) => l.match(e.status));
      if (lane) g[lane.id].push(e);
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => b.expense_date.localeCompare(a.expense_date));

    // Snapshot pre-recency-filter counts for the lane header's "X av Y".
    const totals = {};
    for (const lane of LANES) totals[lane.id] = g[lane.id].length;

    // Recency filter — terminal lanes only. Picks the right "last touched"
    // date per lane: approved_at for Godkända, settled_at-or-updated_at for
    // Betalda, updated_at for Avvisade (no rejected_at column on expense).
    const cutoffFromDays = (days) => {
      const c = new Date();
      c.setDate(c.getDate() - days);
      return c;
    };
    const recencyFilter = (items, days, dateOf) => {
      if (!days || days <= 0) return items;
      const cutoff = cutoffFromDays(days);
      return items.filter(e => {
        const d = dateOf(e);
        return d && new Date(d) >= cutoff;
      });
    };
    g.approved = recencyFilter(g.approved, approvedDays, e => e.approved_at || e.updated_at);
    g.paid     = recencyFilter(g.paid,     paidDays,     e => e.settled_at || e.booked_at || e.updated_at);
    g.rejected = recencyFilter(g.rejected, rejectedDays, e => e.updated_at);

    return { grouped: g, laneTotals: totals };
  }, [filtered, approvedDays, paidDays, rejectedDays]);

  const stats = useMemo(() => {
    const sum = (arr) => arr.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return {
      draft: expenses.filter(e => e.status === 'draft').length,
      total: sum(expenses),
      approved: sum(expenses.filter(e => e.status === 'approved')),
      billable: sum(expenses.filter(e => e.is_billable)),
    };
  }, [expenses]);

  const isValidDropTarget = (laneKey) => {
    if (!dragSourceStatus) return true;
    return DRAG_TRANSITIONS[dragSourceStatus]?.includes(laneKey) || false;
  };

  const handleDragStart = (start) => {
    const exp = expenses.find(e => e.expense_id === start.draggableId);
    if (exp) setDragSourceStatus(exp.status);
  };

  const handleDragEnd = (result) => {
    setDragSourceStatus(null);
    const { destination, draggableId } = result;
    if (!destination) return;
    const from = result.source.droppableId;
    const to = destination.droppableId;
    if (from === to) return;
    if (!DRAG_TRANSITIONS[from]?.includes(to)) return;

    if (from === 'draft' && to === 'submitted') {
      handleSubmit(draggableId);
    } else if (to === 'approved') {
      handleApprove(draggableId);
    } else if (to === 'rejected') {
      setRejectingId(draggableId);
    }
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}><Spinner animation="border" variant="primary" /></div>;

  return (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }

        .expense-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; }
        .expense-stat-card { background: white; border-radius: 8px; padding: 1.25rem; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.06); transition: box-shadow 0.2s; }
        .expense-stat-card:hover { box-shadow: 0 6px 24px rgba(0,0,0,0.1); }
        .esc-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.75rem; }
        .esc-value { font-size: 1.375rem; font-weight: 700; margin-bottom: 2px; font-variant-numeric: tabular-nums; }
        .esc-label { font-size: 0.6875rem; font-weight: 500; color: var(--secondary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
        .esc-sub { font-size: 0.6875rem; color: var(--secondary); margin-top: 2px; }

        .expense-board { display: flex; gap: 0.75rem; align-items: flex-start; overflow-x: auto; padding-bottom: 1rem; }
        .expense-lane { flex: 1; min-width: 0; background: #f8f9fb; border-radius: 12px; display: flex; flex-direction: column; max-height: calc(100vh - 340px); min-height: 300px; transition: all 0.2s; }
        .expense-lane.lane-empty { background: transparent; border: 2px dashed #b0b8c4; min-height: 180px; opacity: 0.6; }
        .expense-lane.lane-empty .exp-lane-header { background: transparent; }
        .expense-lane.lane-drag-over { background: rgba(59,130,246,0.06); box-shadow: inset 0 0 0 2px var(--primary); }
        .expense-lane.lane-drop-invalid { opacity: 0.3; filter: grayscale(0.5); }
        .exp-lane-header { padding: 0.75rem 0.875rem; background: white; border-radius: 12px 12px 0 0; border-bottom: 1px solid #f1f5f9; }
        .exp-lane-count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; border-radius: 10px; font-size: 0.6875rem; font-weight: 600; padding: 0 0.375rem; }
        .exp-lane-cards { padding: 0.375rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 0.375rem; }
        .exp-lane-cards::-webkit-scrollbar { width: 4px; }
        .exp-lane-cards::-webkit-scrollbar-track { background: transparent; }
        .exp-lane-cards::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
        .exp-lane-empty { text-align: center; padding: 1.25rem 0.5rem; color: var(--secondary); font-size: 0.6875rem; opacity: 0.5; }

        .expense-card { background: white; border: 1px solid #e8ecf1; border-radius: 8px; padding: 0.625rem 0.75rem; cursor: grab; transition: all 0.15s; user-select: none; }
        .expense-card:hover { border-color: var(--primary); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
        .expense-card.is-dragging { box-shadow: 0 12px 28px rgba(0,0,0,0.15); border-color: var(--primary); opacity: 0.95; }
        .expense-card.drag-disabled { cursor: default; }
        .drag-handle { color: #d1d5db; opacity: 0; transition: opacity 0.15s; flex-shrink: 0; }
        .expense-card:hover .drag-handle { opacity: 1; }

        .receipt-dropzone { border: 2px dashed #d1d5db; border-radius: 10px; padding: 20px; text-align: center; cursor: pointer; transition: all 0.2s; background: #fafbfc; }
        .receipt-dropzone:hover { border-color: var(--primary); background: var(--rgba-primary-1); }
        .receipt-dropzone.has-file { border-color: var(--primary); border-style: solid; background: var(--rgba-primary-1); }

        .board-search { position: relative; }
        .board-search input { padding-left: 2.25rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.875rem; height: 38px; width: 260px; transition: all 0.2s; background: white; }
        .board-search input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); outline: none; }
        .board-search .search-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--secondary); opacity: 0.5; }

        @media (max-width: 992px) { .expense-stats-grid { grid-template-columns: repeat(2, 1fr); } }

        /* ─── View toggle (kanban / list) ─── */
        .exp-view-toggle {
          display: inline-flex;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          height: 38px;
        }
        .exp-view-btn {
          width: 36px;
          border: none;
          border-right: 1px solid #e2e8f0;
          background: white;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
        }
        .exp-view-btn:last-child { border-right: none; }
        .exp-view-btn:hover:not(.active) { background: #f3f4f6; }
        .exp-view-btn.active { background: var(--rgba-primary-1); color: var(--bs-primary); }

        /* ─── Status pills ─── */
        .exp-pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
        .exp-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border: 1px solid #d1d5db; background: white;
          border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
          color: #111827; cursor: pointer; transition: all 0.15s;
        }
        .exp-pill:hover { background: #f3f4f6; }
        .exp-pill.active {
          background: var(--rgba-primary-2); color: var(--bs-primary);
          border-color: var(--bs-primary); font-weight: 600;
        }
        .exp-pill-count {
          font-size: 0.6875rem; padding: 1px 6px; border-radius: 999px;
          background: #e5e7eb; color: #374151; font-weight: 600;
        }
        .exp-pill.active .exp-pill-count { background: var(--bs-primary); color: white; }

        /* ─── List view ─── */
        .exp-list { background: white; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
        .exp-list-header, .exp-list-row {
          display: grid;
          grid-template-columns: 110px 1fr 160px 110px 110px 60px;
          gap: 12px; padding: 10px 14px; align-items: center;
          font-size: 0.8125rem;
        }
        .exp-list-header {
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 0.6875rem; font-weight: 600; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .exp-list-row { border-bottom: 1px solid #f1f5f9; transition: background-color 0.15s; }
        .exp-list-row:hover { background: #f8fafc; }
        .exp-list-row:last-child { border-bottom: none; }
        .exp-list-amt { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; }
        .exp-list-desc { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .exp-list-desc-text { color: #0f172a; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .exp-list-project { font-size: 0.6875rem; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .exp-list-actions { display: flex; gap: 4px; justify-content: flex-end; }
        .exp-row-action {
          width: 28px; height: 28px; border-radius: 6px;
          background: white; border: 1px solid #e5e7eb; color: #6b7280;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
        }
        .exp-row-action:hover { background: #fee2e2; border-color: #ef4444; color: #991b1b; }

        .exp-empty {
          text-align: center; padding: 48px 16px; color: #9ca3af;
          background: white; border: 1px dashed #e5e7eb; border-radius: 10px;
        }

        /* ─── Per-lane recency dropdown (matches the time-reporting one) ─── */
        .exp-lane-date-filter {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.25rem 0.375rem;
          font-size: 0.6875rem;
          color: var(--secondary);
          background: #f8f9fb;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
        }
        .exp-lane-date-filter:focus { border-color: var(--primary); }
      `}</style>

      <PageTitle activeMenu="Utlägg" motherMenu="Utlägg" />
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats */}
      <div className="expense-stats-grid" style={{ marginBottom: 20 }}>
        <div className="expense-stat-card">
          <div className="esc-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}><Receipt size={18} /></div>
          <div className="esc-value" style={{ color: 'var(--primary)' }}>{fmtAmount(stats.total)}</div>
          <div className="esc-label">Totalt</div>
          <div className="esc-sub">{expenses.length} utlägg</div>
        </div>
        <div className="expense-stat-card">
          <div className="esc-icon" style={{ background: '#fffbeb', color: '#d97706' }}><Clock size={18} /></div>
          <div className="esc-value" style={{ color: '#d97706' }}>{stats.draft}</div>
          <div className="esc-label">Utkast</div>
          <div className="esc-sub">ej inskickade</div>
        </div>
        <div className="expense-stat-card">
          <div className="esc-icon" style={{ background: 'rgba(5,150,105,0.08)', color: '#059669' }}><Check size={18} /></div>
          <div className="esc-value" style={{ color: '#059669' }}>{fmtAmount(stats.approved)}</div>
          <div className="esc-label">Godkända</div>
          <div className="esc-sub">godkänt belopp</div>
        </div>
        <div className="expense-stat-card">
          <div className="esc-icon" style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }}><DollarSign size={18} /></div>
          <div className="esc-value" style={{ color: '#3b82f6' }}>{fmtAmount(stats.billable)}</div>
          <div className="esc-label">Debiterbart</div>
          <div className="esc-sub">kan faktureras</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="board-search">
            <Search size={16} className="search-icon" />
            <input type="text" placeholder="Sök beskrivning, projekt..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.875rem', border: '1px solid #e2e8f0', borderRadius: '8px', height: '38px' }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="all">Alla kategorier</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
          </select>
          <div className="exp-view-toggle">
            <button
              type="button"
              className={`exp-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => changeView('kanban')}
              aria-label="Kanban-vy"
              title="Kanban"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              type="button"
              className={`exp-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => changeView('list')}
              aria-label="Listvy"
              title="Lista"
            >
              <ListIcon size={14} />
            </button>
          </div>
          <button onClick={() => loadData(true)} disabled={refreshing} style={{ height: 38, width: 38, padding: 0, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} style={{ color: 'var(--secondary)' }} />
          </button>
        </div>
        <div className="d-flex gap-2">
          <button onClick={() => setShowForm(true)} style={S.btnPrimary}><Plus size={14} /> Nytt utlägg</button>
        </div>
      </div>

      {/* Status pills — list mode only. Kanban already shows every lane. */}
      {viewMode === 'list' && (
        <div className="exp-pills">
          <button
            type="button"
            className={`exp-pill ${listStatus === 'all' ? 'active' : ''}`}
            onClick={() => setListStatus('all')}
          >
            Alla
            <span className="exp-pill-count">{filtered.length}</span>
          </button>
          {LANES.map(lane => {
            const n = filtered.filter(e => lane.match(e.status)).length;
            return (
              <button
                key={lane.id}
                type="button"
                className={`exp-pill ${listStatus === lane.id ? 'active' : ''}`}
                onClick={() => setListStatus(lane.id)}
              >
                {lane.label}
                {n > 0 && <span className="exp-pill-count">{n}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* List view — compact table for bulk scan / search workflows. */}
      {viewMode === 'list' && (() => {
        const rows = filtered
          .filter(e => listStatus === 'all' || (LANES.find(l => l.id === listStatus)?.match(e.status)))
          .sort((a, b) => (b.expense_date || '').localeCompare(a.expense_date || ''));
        if (rows.length === 0) {
          return <div className="exp-empty">Inga utlägg matchar filtret.</div>;
        }
        return (
          <div className="exp-list">
            <div className="exp-list-header">
              <div>Datum</div>
              <div>Beskrivning</div>
              <div>Kategori</div>
              <div className="exp-list-amt">Belopp</div>
              <div>Status</div>
              <div />
            </div>
            {rows.map((e) => {
              const lane = LANES.find(l => l.match(e.status));
              const cat = catMap[e.category];
              return (
                <div
                  key={e.expense_id}
                  className="exp-list-row"
                  onClick={() => setDetailExpense(e)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>{e.expense_date}</div>
                  <div className="exp-list-desc">
                    <span className="exp-list-desc-text">{e.description || '—'}</span>
                    {e.project_name && (
                      <span className="exp-list-project">{e.project_name}</span>
                    )}
                  </div>
                  <div>
                    {cat && <span style={{ marginRight: 4 }}>{cat.icon}</span>}
                    {cat?.label || e.category}
                  </div>
                  <div className="exp-list-amt">{fmtAmount(e.amount)}</div>
                  <div>
                    {lane && (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: lane.bg,
                        color: lane.color,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                      }}>
                        {lane.label}
                      </span>
                    )}
                  </div>
                  <div className="exp-list-actions" onClick={(ev) => ev.stopPropagation()}>
                    {e.status === 'draft' && !e.payroll_id && !e.invoice_item_id && (
                      <button
                        type="button"
                        className="exp-row-action"
                        onClick={() => handleDelete(e.expense_id)}
                        title="Ta bort"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Kanban Board */}
      {viewMode === 'kanban' && (
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="expense-board">
          {LANES.filter(lane => {
            // Hide Avvisade lane when empty unless a drag from "submitted"
            // is in progress — drop target must be reachable then.
            if (lane.id !== 'rejected') return true;
            if ((grouped.rejected || []).length > 0) return true;
            return dragSourceStatus === 'submitted';
          }).map(lane => {
            const items = grouped[lane.id] || [];
            const isEmpty = items.length === 0;
            const isValidDrop = isValidDropTarget(lane.id);
            const LaneIcon = lane.icon;
            const laneTotal = items.reduce((s, e) => s + (Number(e.amount) || 0), 0);
            const isTerminal = lane.id === 'approved' || lane.id === 'paid' || lane.id === 'rejected';
            const days = lane.id === 'approved' ? approvedDays : lane.id === 'paid' ? paidDays : lane.id === 'rejected' ? rejectedDays : 0;
            const total = laneTotals[lane.id] || 0;
            const showRatio = isTerminal && days > 0 && total > items.length;

            return (
              <Droppable droppableId={lane.id} key={lane.id} isDropDisabled={!isValidDrop}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef} {...provided.droppableProps}
                    className={[
                      'expense-lane',
                      isEmpty && !snapshot.isDraggingOver ? 'lane-empty' : '',
                      snapshot.isDraggingOver ? 'lane-drag-over' : '',
                      dragSourceStatus && !isValidDrop ? 'lane-drop-invalid' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="exp-lane-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <LaneIcon size={13} style={{ color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : lane.color }} />
                          <span style={{ fontWeight: 600, fontSize: '0.75rem', color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : 'var(--title)' }}>{lane.label}</span>
                        </div>
                        {!isEmpty && <span className="exp-lane-count" style={{ background: lane.bg, color: lane.color }}>{showRatio ? `${items.length} av ${total}` : items.length}</span>}
                      </div>
                      {!isEmpty && laneTotal > 0 && (
                        <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: lane.color, marginTop: 2 }}>
                          {fmtAmount(laneTotal)} SEK
                        </div>
                      )}
                      {isTerminal && (
                        <select
                          className="exp-lane-date-filter"
                          value={days}
                          onChange={(ev) => {
                            const v = Number(ev.target.value);
                            if (lane.id === 'approved') setApprovedDays(v);
                            else if (lane.id === 'paid') setPaidDays(v);
                            else setRejectedDays(v);
                          }}
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          <option value={7}>Senaste 7 dagar</option>
                          <option value={30}>Senaste 30 dagar</option>
                          <option value={90}>Senaste 90 dagar</option>
                          <option value={0}>Visa alla</option>
                        </select>
                      )}
                    </div>
                    <div className="exp-lane-cards">
                      {isEmpty && !snapshot.isDraggingOver ? (
                        <div className="exp-lane-empty">
                          {dragSourceStatus && isValidDrop ? 'Släpp här' : `Inga ${lane.label.toLowerCase()}`}
                        </div>
                      ) : (
                        items.map((expense, index) => {
                          const isDragDisabled = saving || DRAG_TRANSITIONS[lane.id]?.length === 0;
                          return (
                            <Draggable key={expense.expense_id} draggableId={expense.expense_id} index={index} isDragDisabled={isDragDisabled}>
                              {(provided, snapshot) => {
                                const child = (
                                  <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                    <ExpenseCard expense={expense} isDragging={snapshot.isDragging} onDelete={handleDelete} onOpen={setDetailExpense} />
                                  </div>
                                );
                                return snapshot.isDragging ? ReactDOM.createPortal(child, document.body) : child;
                              }}
                            </Draggable>
                          );
                        })
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
      )}

      {/* ─── CREATE EXPENSE MODAL ─── */}
      <Modal show={showForm} onHide={closeForm} centered size="lg">
        <Modal.Header style={S.modalHeader}>
          <div>
            <div style={S.modalTitle}>Nytt utlägg</div>
            <div style={S.modalSubtitle}>Fota eller ladda upp kvitto för automatisk ifyllning</div>
          </div>
        </Modal.Header>
        <Modal.Body style={S.modalBody}>
          {/* Receipt upload zone — react-dropzone v11 takes `accept` as a
              comma-separated MIME string (the v14+ object syntax silently
              rejected everything). When a file is attached, we show an
              inline thumbnail so the user sees exactly what was uploaded. */}
          <div style={{ marginBottom: 20 }}>
            <Dropzone
              onDrop={files => { if (files[0]) handleScanReceipt(files[0]); }}
              accept="image/*,application/pdf"
              multiple={false}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className={`receipt-dropzone${form.receiptFile ? ' has-file' : ''}`}>
                  <input {...getInputProps()} />
                  {scanning ? (
                    <div>
                      <Spinner animation="border" size="sm" variant="primary" style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>Skannar kvitto...</div>
                    </div>
                  ) : form.receiptFile ? (
                    <ReceiptThumbnail file={form.receiptFile} />
                  ) : (
                    <div>
                      <Camera size={24} style={{ color: S.colors.textLight, marginBottom: 8 }} />
                      <div style={{ fontSize: 13, fontWeight: 500, color: S.colors.textSecondary }}>Dra hit kvitto eller klicka för att välja</div>
                      <div style={{ fontSize: 11, color: S.colors.textLight, marginTop: 2 }}>PDF eller bild — vi fyller i fälten automatiskt</div>
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
          </div>

          {/* Wrapper dims and disables everything below while the receipt
              is being OCR'd — prevents the scan from overwriting user edits
              in mid-flight and gives clear visual feedback that the form is
              being filled in. Cancel still works (handled at the footer). */}
          <fieldset disabled={scanning} style={{ border: 0, padding: 0, margin: 0, opacity: scanning ? 0.55 : 1, pointerEvents: scanning ? 'none' : 'auto', transition: 'opacity 0.15s' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={S.modalLabel}>Datum <span style={{ color: S.colors.danger }}>*</span></label>
              <Form.Control type="date" value={form.expenseDate} onChange={e => setForm({ ...form, expenseDate: e.target.value })} style={{ borderRadius: 8, fontSize: 14, height: 42 }} />
            </div>
            <div>
              <label style={S.modalLabel}>Belopp (SEK) <span style={{ color: S.colors.danger }}>*</span></label>
              <Form.Control type="number" step="0.01" min="0" placeholder="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={{ borderRadius: 8, fontSize: 14, height: 42 }} />
              {/* OCR-derived net + VAT hint. Only shown when DI gave us both
                  numbers; purely informational — doesn't mutate the form. */}
              {form.metadata?.ocr && form.metadata.ocr.vatAmount != null && form.metadata.ocr.subtotal != null && (
                <div style={{ fontSize: 11, color: S.colors.textLight, marginTop: 4 }}>
                  netto {form.metadata.ocr.subtotal.toFixed(2)} + moms {form.metadata.ocr.vatAmount.toFixed(2)}
                  {form.metadata.ocr.vatRate != null && ` (${form.metadata.ocr.vatRate}%)`}
                </div>
              )}
              {form.metadata?.ocr && form.metadata.ocr.confidence != null && form.metadata.ocr.confidence < 0.7 && (
                <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                  ⚠ OCR-resultatet är osäkert — kontrollera beloppet och datumet
                </div>
              )}
            </div>
          </div>

          {/* VAT block — required so booking can be deterministic.
              "Beräkna från sats" back-derives moms from amount × rate. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }}>
            <div>
              <label style={S.modalLabel}>
                Moms (SEK) <span style={{ color: S.colors.danger }}>*</span>
                {form.amount && form.vatRate !== '' && (
                  <button
                    type="button"
                    onClick={() => {
                      const total = parseFloat(form.amount) || 0;
                      const rate = parseInt(form.vatRate, 10) || 0;
                      const moms = rate > 0 ? (total * rate) / (100 + rate) : 0;
                      setForm({ ...form, vatAmount: moms.toFixed(2) });
                    }}
                    style={{ marginLeft: 8, fontSize: 11, color: 'var(--primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Beräkna från sats
                  </button>
                )}
              </label>
              <Form.Control
                type="number" step="0.01" min="0" placeholder="0"
                value={form.vatAmount}
                onChange={(e) => setForm({ ...form, vatAmount: e.target.value })}
                style={{ borderRadius: 8, fontSize: 14, height: 42 }}
              />
            </div>
            <div>
              <label style={S.modalLabel}>Momssats <span style={{ color: S.colors.danger }}>*</span></label>
              <Form.Select
                value={form.vatRate}
                onChange={(e) => setForm({ ...form, vatRate: e.target.value })}
                style={{ borderRadius: 8, fontSize: 14, height: 42 }}
              >
                <option value="25">25%</option>
                <option value="12">12%</option>
                <option value="6">6%</option>
                <option value="0">0% (momsfritt)</option>
              </Form.Select>
            </div>
          </div>
          {/* Derived-net preview + exempt warning */}
          {form.amount && form.vatAmount !== '' && (
            <div style={{ fontSize: 11, color: S.colors.textLight, marginBottom: 8 }}>
              Netto {(parseFloat(form.amount) - (parseFloat(form.vatAmount) || 0)).toFixed(2)} kr
              + moms {(parseFloat(form.vatAmount) || 0).toFixed(2)} kr
              {form.vatRate === '0' && form.metadata?.vat?.scheme !== 'eu_reverse' && (
                <span style={{ color: '#b45309', marginLeft: 8 }}>
                  ⚠ De flesta kvitton har moms — är detta korrekt?
                </span>
              )}
            </div>
          )}
          {/* EU reverse-charge toggle — supplier invoiced without VAT (AWS/Zoom/utlandshotell).
              Flips VAT amount to 0 and tags metadata.vat.scheme='eu_reverse' so booking posts
              synthetic 2645/2614 instead of 2641. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#1e40af', marginBottom: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.metadata?.vat?.scheme === 'eu_reverse'}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm({
                  ...form,
                  vatAmount: checked ? '0' : form.vatAmount,
                  metadata: {
                    ...(form.metadata || {}),
                    vat: {
                      ...(form.metadata?.vat || {}),
                      scheme: checked ? 'eu_reverse' : 'domestic',
                    },
                  },
                });
              }}
            />
            🇪🇺 EU-inköp (omvänd skattskyldighet — leverantören fakturerar utan moms)
          </label>
          {!form.amount && <div style={{ marginBottom: 16 }} />}

          <div style={{ marginBottom: 16 }}>
            <label style={S.modalLabel}>Beskrivning <span style={{ color: S.colors.danger }}>*</span></label>
            <Form.Control type="text" placeholder="T.ex. Taxi till kundmöte" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ borderRadius: 8, fontSize: 14, height: 42 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={S.modalLabel}>Kategori</label>
              <Select
                options={CATEGORIES.map(c => ({ value: c.value, label: `${c.icon} ${c.label}` }))}
                value={{ value: form.category, label: `${catMap[form.category]?.icon} ${catMap[form.category]?.label}` }}
                onChange={opt => setForm({ ...form, category: opt?.value || 'other', metadata: null })}
                styles={S.selectStyles} isSearchable={false}
              />
            </div>
            <div>
              <label style={S.modalLabel}>Projekt <span style={{ color: S.colors.textLight, fontWeight: 400 }}>(valfritt)</span></label>
              <Select
                options={projects.map(p => ({ value: p.project_id, label: p.name }))}
                value={form.projectId ? { value: form.projectId, label: projects.find(p => p.project_id === form.projectId)?.name } : null}
                onChange={opt => setForm({ ...form, projectId: opt?.value || '' })}
                isClearable isSearchable placeholder="Inget projekt" styles={S.selectStyles}
              />
            </div>
          </div>

          <ExpenseSubForm form={form} setForm={setForm} />

          <div style={{ display: 'flex', gap: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.colors.textSecondary, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isBillable} onChange={e => setForm({ ...form, isBillable: e.target.checked })} style={{ accentColor: 'var(--primary)' }} />
              Debiterbar
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.colors.textSecondary, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isReimbursable} onChange={e => setForm({ ...form, isReimbursable: e.target.checked })} style={{ accentColor: 'var(--primary)' }} />
              Ersättningsbar
            </label>
          </div>
          </fieldset>
        </Modal.Body>
        <Modal.Footer style={S.modalFooter}>
          <button onClick={closeForm} style={S.btnSecondary}>Avbryt</button>
          <button
            onClick={handleCreate}
            disabled={saving || scanning || !form.description || !form.amount || form.vatAmount === '' || form.vatRate === ''}
            style={{ ...S.btnPrimary, opacity: (form.description && form.amount && form.vatAmount !== '' && form.vatRate !== '' && !scanning) ? 1 : 0.5 }}
          >
            <Receipt size={14} /> Lägg till utlägg
          </button>
        </Modal.Footer>
      </Modal>

      {/* ─── REJECT MODAL ─── */}
      <Modal show={!!rejectingId} onHide={() => { setRejectingId(null); setRejectReason(''); }}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title className="fs-5" style={{ fontWeight: 700 }}>Avvisa utlägg</Modal.Title>
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 4 }}>Ange en anledning</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Control as="textarea" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="T.ex. saknas kvitto..." autoFocus style={{ borderRadius: 8, fontSize: '0.875rem' }} />
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => { setRejectingId(null); setRejectReason(''); }} style={S.btnSecondary}>Avbryt</button>
          <button onClick={() => handleReject(rejectingId)} disabled={!rejectReason.trim() || saving}
            style={{ ...S.btnPrimary, background: S.colors.danger, opacity: rejectReason.trim() ? 1 : 0.5 }}>
            <X size={14} /> Avvisa
          </button>
        </Modal.Footer>
      </Modal>

      {/* ─── EXPENSE DETAIL / EDIT MODAL ─── */}
      <ExpenseDetailModal
        expense={detailExpense}
        companyId={companyId}
        projects={projects}
        onClose={() => setDetailExpense(null)}
        onChanged={async () => { setDetailExpense(null); await loadData(); }}
      />
    </>
  );
};


export default ExpensesPage;
